// context/AuthContext.js
// Last updated: November 27, 2025
// Added: initializePayment and verifyPayment for Paystack integration

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.whydiditdie.com';

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          setUser(null);
          setToken(null);
        } else if (session) {
          setUser(session.user);
          setToken(session.access_token);
        } else {
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user && session?.access_token) {
        setUser(session.user);
        setToken(session.access_token);
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setToken(null);
        setLoading(false);
      } else if (event === 'USER_UPDATED' && session?.user && session?.access_token) {
        setUser(session.user);
        setToken(session.access_token);
      } else if (event === 'INITIAL_SESSION' && session?.user && session?.access_token) {
        setUser(session.user);
        setToken(session.access_token);
        setLoading(false);
      }
    });

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        let errorMessage = 'Login failed. Please check your credentials.';
        if (error.message.includes('Invalid login credentials')) errorMessage = 'Invalid email or password.';
        else if (error.message.includes('Email not confirmed')) errorMessage = 'Please confirm your email before logging in.';
        return { success: false, error: errorMessage };
      }
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback`, queryParams: { access_type: 'offline', prompt: 'consent' } },
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password, fullName, firstName, lastName, company, role, country, phone) => {
    try {
      setLoading(true);
      const normalizedEmail = email.toLowerCase().trim();
      
      // First check if user exists but isn't confirmed
      const { data: { users }, error: listError } = await supabase.auth.admin.listUsers();
      const unconfirmedUser = users?.find(user => 
        user.email === normalizedEmail && 
        user.confirmation_sent_at && 
        !user.email_confirmed_at
      );

      if (unconfirmedUser) {
        // Resend confirmation email
        const { error: resendError } = await supabase.auth.resend({
          type: 'signup',
          email: normalizedEmail,
        });

        if (resendError) {
          return { 
            success: false, 
            error: 'Confirmation email could not be resent. Please try again later.' 
          };
        }

        return { 
          success: true, 
          needsConfirmation: true,
          message: `We've resent the confirmation email to ${normalizedEmail}. Please check your inbox.`
        };
      }

      // Proceed with normal registration
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            full_name: fullName || `${firstName || ''} ${lastName || ''}`.trim(),
            first_name: firstName || '',
            last_name: lastName || '',
            company: company || '',
            role: role || '',
            country: country || '',
            phone: phone || '',
          },
        },
      });
      
      if (error) {
        let errorMessage = 'Registration failed. Please try again.';
        if (error.message.includes('already registered') || error.message.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please try logging in instead.';
        }
        return { success: false, error: errorMessage };
      }
      
      // Successful registration but needs confirmation
      if (data?.user && !data.session) {
        return { 
          success: true, 
          needsConfirmation: true,
          message: `Please check your email (${normalizedEmail}) and click the confirmation link to activate your account.`
        };
      }
      
      // Immediate login case (shouldn't happen with email confirmation enabled)
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const registerWithGoogle = async () => {
    try {
      logger.info('🔄 Starting Google OAuth signup...');
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { 
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: { 
            access_type: 'offline', 
            prompt: 'consent' 
          }
        },
      });
      
      if (error) {
        logger.error('❌ Google OAuth error:', error);
        return { success: false, error: error.message };
      }
      
      logger.info('✅ Google OAuth initiated, user will be redirected to dashboard');
      return { success: true, needsConfirmation: false };
    } catch (error) {
      logger.error('❌ Google signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (!error) {
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const refreshUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        setUser(null);
        setToken(null);
        return null;
      }
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(user);
        setToken(session?.access_token ?? null);
        return user;
      }
      return null;
    } catch (error) {
      setUser(null);
      setToken(null);
      return null;
    }
  };

  // ============================================
  // PAYMENT FUNCTIONS - Paystack Integration
  // ============================================

  const initializePayment = async (planType) => {
    try {
      if (!user?.email) {
        logger.error('💳 No user email found for payment');
        return { success: false, error: 'You must be logged in to subscribe' };
      }

      // Pricing in USD
      const pricing = {
        monthly: 35,
        annual: 331
      };

      const amount = pricing[planType] || pricing.monthly;

      logger.info('💳 Initializing payment:', { 
        email: user.email, 
        amount, 
        planType,
        backendUrl: BACKEND_URL 
      });

      const response = await fetch(`${BACKEND_URL}/api/paystack/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          email: user.email,
          amount: amount,
          plan: 'pro',
          billing_cycle: planType
        })
      });

      logger.info('💳 Payment init response status:', response.status);

      const data = await response.json();
      logger.info('💳 Payment init response data:', data);

      if (data.status && data.authorization_url) {
        return { 
          success: true, 
          authorization_url: data.authorization_url,
          reference: data.reference 
        };
      } else {
        return { 
          success: false, 
          error: data.message || 'Failed to initialize payment' 
        };
      }
    } catch (error) {
      logger.error('💳 Payment initialization error:', error);
      return { success: false, error: error.message || 'Payment initialization failed' };
    }
  };

  const verifyPayment = async (reference) => {
    try {
      logger.info('💳 Verifying payment:', reference);

      const response = await fetch(`${BACKEND_URL}/api/paystack/verify/${reference}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();
      logger.info('💳 Payment verification response:', data);

      if (data.status) {
        // Payment successful - refresh user to get updated subscription status
        await refreshUser();
        return { success: true, data: data.data };
      } else {
        return { success: false, error: data.message || 'Payment verification failed' };
      }
    } catch (error) {
      logger.error('💳 Payment verification error:', error);
      return { success: false, error: error.message || 'Payment verification failed' };
    }
  };

  // ============================================
  // DEBUG & UTILITY FUNCTIONS
  // ============================================

  const getAuthDebugInfo = () => ({
    hasUser: !!user,
    userEmail: user?.email,
    hasToken: !!token,
    isLoading: loading,
    isAuthenticated: !!user,
    backendUrl: BACKEND_URL,
    currentOrigin: window.location.origin,
    authMethod: '100% Supabase',
  });

  const testBackendConnection = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health`);
      if (response.ok) return { success: true, data: await response.json() };
      return { success: false, error: `Backend error: ${response.status}` };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ============================================
  // CONTEXT VALUE - All exported functions
  // ============================================

  const value = {
    // State
    user,
    token,
    loading,
    isAuthenticated: !!user,
    
    // Auth functions
    login,
    loginWithGoogle,
    register,
    registerWithGoogle,
    logout,
    refreshUser,
    
    // Payment functions
    initializePayment,
    verifyPayment,
    
    // Debug/utility
    getAuthDebugInfo,
    testBackendConnection,
  };

  useEffect(() => {
    logger.info('🔍 AuthContext State Update:', {
      isAuthenticated: !!user,
      userEmail: user?.email,
      loading,
      hasToken: !!token,
      backendUrl: BACKEND_URL,
      currentOrigin: window.location.origin,
      authMethod: '100% Supabase',
    });
  }, [user, loading, token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;